
"use client";

import { useState, useEffect, useRef } from "react";
import { useReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { generateNodesBasedOnStructure } from './organization-editor/NodeGenerator';
import { validateAndConvertEdges } from './organization-editor/EdgeOptions';
import { ErrorAlert, DeleteEdgeDialog } from './organization-editor/AlertDialogs';
import { ProjectDetailsOverlay } from './organization-editor/ProjectDetailsOverlay';
import { InstructionsOverlay } from './organization-editor/InstructionsOverlay';
import { ReactFlowContainer } from './organization-editor/ReactFlowContainer';
import { NavigationButtons } from './organization-editor/NavigationButtons';
import { useProjectData } from './organization-editor/hooks/useProjectData';
import { useOrganizationFlow } from './organization-editor/hooks/useOrganizationFlow';
import { useNavigation } from './organization-editor/hooks/useNavigation';
import { supabase } from "@/integrations/supabase/client";

const OrganizationStructureEditorContent = () => {
  const { urlProjectId } = useNavigation();
  const {
    projectName,
    selectedStructure,
    directorsCount,
    managersCount,
    teamMembersCount,
    isEditable,
    setIsEditable,
    showAlert,
    setShowAlert,
    alertTitle,
    alertDescription,
    isError
  } = useProjectData(urlProjectId);

  const {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    onConnect,
    onEdgeClick,
    selectedEdgeId,
    showDeleteEdgeDialog,
    setShowDeleteEdgeDialog,
    handleDeleteEdge,
    handleCancelEdgeDelete,
    handleSaveToSupabase,
    saveTimeoutRef
  } = useOrganizationFlow(isEditable, urlProjectId);

  const { handleBack, handleNext, isSaving, setIsSaving } = useNavigation();

  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [saveAlertTitle, setSaveAlertTitle] = useState("");
  const [saveAlertDescription, setSaveAlertDescription] = useState("");
  const [showSaveRequiredDialog, setShowSaveRequiredDialog] = useState(false);
  const reactFlowInstance = useReactFlow();
  const fitViewTimeoutRef = useRef<NodeJS.Timeout>();

  // Load organization structure data
  useEffect(() => {
    const loadStructureData = async () => {
      if (!urlProjectId) return;

      try {
        const { data: projectData, error: projectError } = await supabase
          .from('project_requirements')
          .select('*')
          .eq('id', urlProjectId)
          .maybeSingle();

        if (projectError || !projectData) return;

        const { data: structureData, error: structureError } = await supabase
          .from('organization_structures')
          .select('*')
          .eq('project_id', projectData.project_id)
          .maybeSingle();

        if (structureError) return;

        setIsEditable(structureData?.is_editable !== false);
        
        if (!structureData || !structureData.is_saved) {
          const generatedNodes = generateNodesBasedOnStructure({
            directors_count: projectData.directors_count,
            managers_count: projectData.managers_count,
            team_members_count: projectData.team_members_count,
            organizational_structure: projectData.organizational_structure
          });
          
          const nodesWithEditFlag = generatedNodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              is_editable: true
            }
          }));
          
          if (!structureData) {
            const { data: session } = await supabase.auth.getSession();
            const userId = session.session?.user.id;
            if (!userId) return;

            await supabase.from('organization_structures').insert({
              project_id: projectData.project_id,
              user_id: userId,
              nodes: JSON.parse(JSON.stringify(nodesWithEditFlag)),
              edges: [],
              name: projectData.name || 'Untitled Project',
              is_saved: false,
              is_editable: true
            });
          }
          setNodes(nodesWithEditFlag);
          setEdges([]);
        } else {
          if (structureData.nodes && Array.isArray(structureData.nodes)) {
            const parsedNodes = JSON.parse(JSON.stringify(structureData.nodes));
            const parsedEdges = validateAndConvertEdges(structureData.edges || []);
            
            const nodesWithEditFlag = parsedNodes.map((node: any) => ({
              ...node,
              data: {
                ...node.data,
                is_editable: structureData.is_editable !== false
              },
              draggable: structureData.is_editable !== false
            }));
            
            setNodes(nodesWithEditFlag);
            setEdges(parsedEdges);
          }
        }
        
        fitViewTimeoutRef.current = setTimeout(() => {
          reactFlowInstance.fitView({ padding: 0.2 });
        }, 100);
      } catch (error) {
        console.error('Error loading structure data:', error);
      }
    };

    loadStructureData();
    
    return () => {
      if (fitViewTimeoutRef.current) {
        clearTimeout(fitViewTimeoutRef.current);
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [urlProjectId, reactFlowInstance, setNodes, setEdges, setIsEditable]);

  const handleNavigationNext = () => {
    handleNext(nodes, edges, handleSaveToSupabase);
  };

  return (
    <div className="space-y-2 h-[570px]">
      <ErrorAlert 
        open={isError && showAlert} 
        onOpenChange={setShowAlert} 
        title={alertTitle} 
        description={alertDescription} 
      />

      <ErrorAlert 
        open={showSaveAlert} 
        onOpenChange={setShowSaveAlert} 
        title={saveAlertTitle} 
        description={saveAlertDescription} 
      />

      <ErrorAlert 
        open={showSaveRequiredDialog} 
        onOpenChange={setShowSaveRequiredDialog} 
        title="Save Required" 
        description="Save changes first before proceeding" 
      />

      <DeleteEdgeDialog 
        open={showDeleteEdgeDialog} 
        onOpenChange={setShowDeleteEdgeDialog} 
        onConfirm={handleDeleteEdge} 
        onCancel={handleCancelEdgeDelete} 
      />

      <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-6 animate-fade-in flex-1 transition-all duration-300 hover:shadow-purple-500/20 p-4 my-[17px] py-[16px] px-[13px]">
        <div 
          style={{ height: '470px' }} 
          className="border-2 border-dashed border-primary/20 rounded-lg overflow-hidden bg-primary/5 relative"
        >
          <ProjectDetailsOverlay
            projectName={projectName}
            selectedStructure={selectedStructure}
            directorsCount={directorsCount}
            managersCount={managersCount}
            teamMembersCount={teamMembersCount}
            isEditable={isEditable}
          />
          
          <InstructionsOverlay isEditable={isEditable} />
          
          <ReactFlowContainer
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            isEditable={isEditable}
          />
        </div>
      </div>

      <NavigationButtons
        onBack={handleBack}
        onNext={handleNavigationNext}
        isSaving={isSaving}
      />
    </div>
  );
};

export const OrganizationStructureEditor = () => {
  return (
    <ReactFlowProvider>
      <OrganizationStructureEditorContent />
    </ReactFlowProvider>
  );
};
